import { oncePer24Hours } from '../../helpers'
import { useAsync } from '../utility'

import { useBuyerSettingsQuery } from './queries'
import { getPricePoint, getMaxBudget } from './getters'
import { useBuyerSettingsMutation } from './mutations'

// Hooks
import { useBranding } from '../branding'

import {
  DEFAULT_MAX_BUDGET,
  DEFAULT_CURRENT_MONTHLY_COST,
  DEFAULT_DOWN_PAYMENT,
  DEFAULT_YEARS_IN_HOME,
  DEFAULT_RENTABLE_NIGHTS,
  BUYER_PREQUAL_PROMPT,
} from '../../constants/buyerSettings'

// Defaulted "safe" data for calculations, consumed by display/copy getters
export const useBuyerSettingsWithDefaults = () => {
  const buyerSettings = useBuyerSettingsQuery()
  const { data = {} } = buyerSettings

  const {
    affordability,
    maxBudget,
    currentAddress,
    currentMonthlyCost,
    yearsInHome,
    downPayment,
    estimatedDownPaymentFromEquity,
    usingDefaultDownPayment,
    hasUpdatedProfileSettings,
    rentableNights,
  } = data ?? {}

  const pricePoint = getPricePoint(affordability, maxBudget)
  const budget = getMaxBudget(affordability, maxBudget)

  return {
    affordability: pricePoint,
    maxBudget: budget ?? DEFAULT_MAX_BUDGET,
    currentMonthlyCost: currentMonthlyCost ?? DEFAULT_CURRENT_MONTHLY_COST,
    yearsInHome: yearsInHome ?? DEFAULT_YEARS_IN_HOME,
    downPayment: downPayment ?? DEFAULT_DOWN_PAYMENT,
    rentableNights: rentableNights ?? DEFAULT_RENTABLE_NIGHTS,
    currentAddress,
    estimatedDownPaymentFromEquity,
    usingDefaultDownPayment,
    hasUpdatedListingsBotProfile: hasUpdatedProfileSettings,
    isFetched: buyerSettings.isFetched,
    isError: buyerSettings.isError,
    error: buyerSettings.error,
    refetch: buyerSettings.refetch,
  }
}

// For display purposes
export const useBuyerSettings = () => {
  const buyerSettings = useBuyerSettingsQuery()

  const {
    clientType,
    currentAddress,
    affordability,
    maxBudget,
    currentMonthlyCost,
    yearsInHome,
    downPayment,
    rentableNights,
    eligibleForPrequal,
    hasUpdatedProfileSettings,
  } = buyerSettings?.data ?? {}

  return {
    clientType,
    currentAddress,
    affordability,
    maxBudget,
    currentMonthlyCost,
    yearsInHome,
    downPayment,
    rentableNights,
    eligibleForPrequal,
    isFetched: buyerSettings.isFetched,
    hasUpdatedListingsBotProfile: hasUpdatedProfileSettings,
  }
}

const useShouldRequestPrequal = () => {
  const { eligibleForPrequal } = useBuyerSettings()
  const { isSoloRea } = useBranding()
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {}
  const result =
    useAsync(async () => oncePer24Hours(BUYER_PREQUAL_PROMPT), {
      executeOnMount: true,
    }) ?? {}

  return {
    ...result,
    execute: eligibleForPrequal ? result?.execute : noop,
    value: eligibleForPrequal && !isSoloRea && !!result?.executeValue,
  }
}

export {
  useBuyerSettingsQuery,
  useBuyerSettingsMutation,
  useShouldRequestPrequal,
}
